import { Link, useNavigate } from 'react-router-dom';
import type { NavLinkDto, SiteSettingsDto } from '@lex/contracts';
import { Button } from '@lex/ui';
import { LockIcon } from '../icons';
import { Emblem, Glow, StatusPill } from '../home/primitives';

function isExternal(href: string): boolean {
  return /^(https?:|tel:|mailto:)/.test(href);
}

function FooterLink({ link }: { link: NavLinkDto }) {
  if (isExternal(link.href)) {
    return (
      <a
        href={link.href}
        className="text-[15px] font-medium text-white/90 transition-colors hover:text-gold-300"
        {...(link.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link
      to={link.href}
      className="text-[15px] font-medium text-white/90 transition-colors hover:text-gold-300"
    >
      {link.label}
    </Link>
  );
}

export function SiteFooter({ settings }: { settings: SiteSettingsDto }) {
  const navigate = useNavigate();
  const go = (href: string): void => {
    if (isExternal(href)) {
      window.location.assign(href);
      return;
    }
    void navigate(href);
  };

  return (
    <footer className="px-4 pb-10 pt-10 md:px-8 md:pb-14 md:pt-16 xl:px-20">
      <div className="relative mx-auto max-w-[1280px] overflow-hidden rounded-[28px] bg-ink-gradient p-6 md:rounded-4xl md:p-12">
        <Glow color="gold" size={420} opacity={0.22} blur={60} className="-right-10 -top-32" />
        <Glow color="blue" size={460} opacity={0.3} blur={60} className="-left-28 top-40" />

        <div className="relative z-10 flex flex-col gap-8 md:gap-10">
          <div className="grid gap-9 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-12">
            <div className="flex flex-col items-start gap-4 md:max-w-[400px]">
              <Link to="/" className="flex items-center gap-3" aria-label="lex.ai — на главную">
                <Emblem />
                <span className="text-[24px] font-bold tracking-[-0.01em] text-white">lex.ai</span>
              </Link>
              <p className="text-[15px] leading-[1.55] text-muted-sky">
                Юридическая помощь призывникам и военнослужащим. Онлайн-чат с юристом без очередей и
                лишних формальностей.
              </p>
              {settings.hoursText ? (
                <StatusPill tone="dark" className="w-full justify-start text-left">
                  {settings.hoursText}
                </StatusPill>
              ) : null}
              <Button
                variant="light"
                size="lg"
                className="rounded-xl"
                onClick={() => go(settings.primaryCta.href)}
              >
                {settings.primaryCta.label}
              </Button>
            </div>

            {settings.footerColumns.map((column) => (
              <div key={column.heading} className="flex flex-col items-start gap-3.5">
                <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-gold-300">
                  {column.heading}
                </span>
                {column.links.map((link) => (
                  <FooterLink key={`${column.heading}-${link.label}`} link={link} />
                ))}
              </div>
            ))}
          </div>

          <div className="flex items-start gap-4 rounded-2xl border border-white/16 bg-white/8 p-5">
            <LockIcon className="mt-0.5 h-5 w-5 shrink-0 text-gold-300" />
            <div className="flex flex-col gap-1">
              <span className="text-[15px] font-semibold text-white md:text-base">
                {settings.footerNoteTitle}
              </span>
              <p className="whitespace-pre-line text-[13px] leading-[1.55] text-muted-sky md:text-sm">
                {settings.footerNoteText}
              </p>
            </div>
          </div>

          <div className="h-px w-full bg-white/14" />

          <div className="flex flex-col gap-3 text-[12px] leading-[1.5] text-muted-sky md:flex-row md:items-start md:gap-10 md:text-[13px]">
            <span className="shrink-0">{settings.copyright}</span>
            <p className="flex-1">{settings.disclaimer}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
